import {CLIENT_MESSAGE, SERVER_MESSAGE} from "@redbrick-sdk/multiplay-common";
import * as Colyseus from "colyseus.js";
import * as THREE from "three";
import {clamp, lerp} from "three/src/math/MathUtils";
import {AVATAR_ANIMATION, URL} from "utils/constants";
import EventManager from "utils/UserScript/RedbrickBuiltin/RedbrickModule/EventManager.js";
import Avatar from "./Assets/Avatar.js";

class GameServerV1 {
  constructor(context) {
    this.context = context;
    this.avatarReady = false;
    this.serverReady = false;
    this.clientReady = false;
    this.prevPos = {x: 0, y: 0, z: 0};
    this.prevQuat = {x: 0, y: 0, z: 0, w: 0};
    this.timeOffset = 0;
    this.connectToServer();

    this._vector1 = new THREE.Vector3();
    this._vector2 = new THREE.Vector3();
    this._quaternion1 = new THREE.Quaternion();
    this._quaternion2 = new THREE.Quaternion();
    this._euler = new THREE.Euler();
  }

  async connectToServer() {
    this.client = new Colyseus.Client(URL.COLYSEUS);
    // [ Local Test ]
    // this.client = new Colyseus.Client("ws://localhost:2567");
  }

  async joinOrCreateRoom() {
    try {
      if (this.context.isGuestMode) return;
      this.rooms = await this.client.getAvailableRooms("my_room_v1");
      // TMP code to match the room and pid
      const pIdLength = this.context.pId.length;
      let roomId = null;
      for (let i = 0; i < this.rooms.length; i++) {
        if (this.rooms[i].roomId.substring(0, pIdLength) === this.context.pId) {
          roomId = this.rooms[i].roomId;
          break;
        }
      }
      // Need Maxclient setting interface
      let maxClients = this.context.scenePlay.userData.maxClients || 20;
      // let maxClients = 20;

      if (!roomId) {
        let options = {
          pid: this.context.pId,
          url: URL.S3_WORLD_STORAGE + "/" + this.context.pId + ".json",
          emote: null,
          maxClients: maxClients,
          token: localStorage.getItem("redbrickToken"),
        };

        // fake server fetch progress
        let imitateProgress = 0;
        const imitateProgressInterval = setInterval(() => {
          if (imitateProgress < 99) {
            this.context.dispatchEvent(
              new CustomEvent("LOADING_IN_PROGRESS", {
                detail: {
                  object: {
                    progress: imitateProgress,
                    processName: "SERVER_SCENE_FETCH",
                  },
                },
              }),
            );
            imitateProgress += 1;
          }
        }, 32);
        this.room = await this.client.create("my_room_v1", options);
        this.room.players = {[this.room.sessionId]: this.context.avatar.model};
        EventManager.setRoomInstance(this.room);
        clearInterval(imitateProgressInterval);
        this.context.dispatchEvent(
          new CustomEvent("LOADING_IN_PROGRESS", {
            detail: {
              object: {progress: 100, processName: "SERVER_SCENE_FETCH"},
            },
          }),
        );
        console.log("[Server]: create room ", this.context.pId, " ok.");
      } else {
        this.room = await this.client.joinById(roomId);
        this.room.players = {[this.room.sessionId]: this.context.avatar.model};
        EventManager.setRoomInstance(this.room);
        console.log("[Server]: join room ", roomId, " ok.");
      }
      // this.apiMessageHandler();
      this.serverMessageHandler();
      this.stateEventHandler();
    } catch (e) {
      console.error(e);
    }
  }

  sendInputToServer(type, input) {
    if (!this.room) return;

    switch (type) {
      case "move":
        if (!this.context.avatar.model.body) return;
        const {x: px, y: py, z: pz} = this.context.avatar.model.body.position;
        const {x: rx, y: ry, z: rz} = this.context.avatar.model.body.rotation;
        if (
          this.prevPos.x !== px ||
          this.prevPos.y !== py ||
          this.prevPos.z !== pz ||
          this.prevRot.x !== rx ||
          this.prevRot.y !== ry ||
          this.prevRot.z !== rz
        ) {
          this.prevPos = {x: px, y: py, z: pz};
          this.prevRot = {x: rx, y: ry, z: rz};
          this.room.send(CLIENT_MESSAGE.MOVE_INPUT, {
            pos: {x: px, y: py, z: pz},
            rot: {x: rx, y: ry, z: rz},
          });
        }
        break;
      case "signal":
        this.room.send("signal", input);
        break;
      default:
        break;
    }
  }

  sendAnimationMessage(animationName) {
    this.room.send(CLIENT_MESSAGE.SEND_ANIMATION, {animationName});
  }

  serverMessageHandler() {
    this.room.onMessage(SERVER_MESSAGE.SERVER_READY, () => {
      this.requestAvatarSetup(true, false);
    });
    this.room.onMessage(SERVER_MESSAGE.AVATAR_READY, () => {
      console.log("[Server]: My Avatar is Ready");
      this.avatarReady = true;
    });
    this.room.onMessage(SERVER_MESSAGE.BROADCAST_ANIMATION, message => {
      const target = this.context.syncObjects[`avatar(${message.id})`];
      if (!target) return;
      target.manager.playAnimation(message.animationName);
    });
    // this.room.onMessage(CLIENT_MESSAGE.AVATAR_EMOTE_CHANGED, message => {
    //   const target = this.context.syncObjects[`avatar(${message.id})`];
    //   if (!target) return;
    //   target.manager.emoteAnimation = message.info;
    // });
    // this.room.onMessage("userscript_error", message => {
    //   let alertMessage = "-- SCRIPT ERROR --";
    //   message.forEach(msg => {
    //     alertMessage += `\n[${msg[0]}] ${msg[1]}`;
    //   });
    //   alert(alertMessage);
    // });
  }

  stateEventHandler() {
    this.room.state.objectStates.onAdd = this.onObjectStateAdded.bind(this);
    this.room.state.objectStates.onRemove = this.onObjectStateRemoved.bind(this);
    this.room.onStateChange(() => {
      this.timeOffset = 0;
    });
  }

  requestAvatarSetup(serverReady, clientReady) {
    if (serverReady === true) this.serverReady = true;
    if (clientReady === true) this.clientReady = true;
    if (this.serverReady === true && this.clientReady === true) {
      const token = localStorage.getItem("redbrickToken");
      const ci = this.context.userInfo.ci;
      const options = {
        token: token,
        avatarId: this.context.userInfo.avatarInfo[0].id,
        userName: this.context.userInfo.name,
        ci: ci,
      };
      this.room.send(CLIENT_MESSAGE.CREATE_AVATAR, options);
    }
  }

  async onObjectStateAdded(objectState, key) {
    if (objectState.type === "AVATAR") {
      const avatar = await this.createAvatarByState(objectState, key);
      const clientId = key.split("(")[1].split(")")[0];
      avatar.name = key;
      avatar.title = key;
      avatar.body.title = key;
      avatar.clientId = clientId;
      avatar.userData.type = "avatar";
      avatar.stateBuffer = [];
      this.context.syncObjects[key] = avatar;

      this.room.players[clientId] = avatar;
      this.context.scriptLauncher.onJoin(clientId, avatar);

      if (!this.context.playerStates) this.context.playerStates = {};
      this.context.playerStates[key] = objectState;
      objectState.onChange = this.handleOnChangeAvatarState.bind(this, this.context.syncObjects[key], objectState);
    } else if (objectState.type === "OBJECT3D" && objectState.name !== "PERSPECTIVE") {
      objectState.onChange = this.handleOnChangeObjectState.bind(this, this.context.syncObjects[key], objectState);
    }
  }

  onObjectStateRemoved(objectState, key) {
    if (objectState.type === "AVATAR") {
      const clientId = key.split("(")[1].split(")")[0];
      delete this.room.players[clientId];
      this.context.scriptLauncher.onLeave(clientId);

      this.context.physics.destroy(this.context.syncObjects[key].body);
      this.context.syncObjects[key].manager.removeNickname();
      delete this.context.playerStates[key];
    }
    this.context.scenePlay.remove(this.context.syncObjects[key]);
    delete this.context.syncObjects[key];
  }

  async createAvatarByState(state, key) {
    if (key === `avatar(${this.room.sessionId})`) {
      // [TMP] to run OOBC script in multiplay
      if (this.context.scenePlay.userData.codeEditorMode === "oobc") {
        Avatar.instance.model.body.name = key;
        await this.context.runScript();
      }

      return Avatar.instance.model;
    }

    const avatar = new Avatar(this.context, false);
    const {avatarId} = state.avatarModel;
    const {userName, ci} = state.clientInfo;

    await avatar.loadForMulti(avatarId, ci);
    await avatar.loadSocketMap();

    avatar.attachNickname(userName);

    this.context.scenePlay.add(avatar.model);

    return avatar.model;
  }

  handleOnChangeAvatarState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "clientInfo":
          break;
        case "avatarModel":
          break;
        default:
          if (state.title === `avatar(${this.room.sessionId})` && change.field === "ts") break;
          this.objectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  objectStateIterator(syncObject, state, change) {
    switch (change.field) {
      case "ts":
        const buffer = {
          ts: state.ts,
          pos: {...state.position},
          quat: {...state.quaternion},
          // ignorePositionLerping:
          //   [...state.forcedPositionTS].filter(ts => ts > syncObject.stateBuffer[syncObject.stateBuffer.length - 1]?.ts).length > 0,
          // ignoreRotationLerping:
          //   [...state.forcedRotationTS].filter(ts => ts > syncObject.stateBuffer[syncObject.stateBuffer.length - 1]?.ts).length > 0,
        };
        syncObject.stateBuffer.push(buffer);
        break;
      // case "collisionFlag":
      //   if (syncObject.body) {
      //     syncObject.body.setCollisionFlags(state.collisionFlag);
      //   }
      //   break;
      case "isAlive":
        if (syncObject.userData.playInfo === undefined) return;
        if (change.value === true) {
          if (syncObject.userData.playInfo.isAlive === true) return;
          syncObject.userData.playInfo.isAlive = true;
          syncObject.traverse(child => {
            child.layers.mask = syncObject.userData.playInfo.layerMask;
          });
          if (syncObject.body) {
            syncObject.body.setCollisionFlags(syncObject.userData.playInfo.collisionFlag);
          }
        } else {
          if (syncObject.userData.playInfo.isAlive === false) return;
          syncObject.userData.playInfo.isAlive = false;
          syncObject.userData.playInfo.layerMask = syncObject.layers.mask;
          syncObject.traverse(child => {
            child.layers.disableAll();
            if (child.type === "Audio") child.stop();
          });
          if (syncObject.body) {
            syncObject.userData.playInfo.collisionFlag = syncObject.body.getCollisionFlags();
            syncObject.body.setCollisionFlags(5);
          }
        }
        break;
      default:
        break;
    }
  }

  handleOnChangeObjectState(syncObject, state, changes) {
    changes.forEach(change => {
      this.objectStateIterator(syncObject, state, change);
    });
  }

  updateSyncObjectsMovement(dt) {
    if (this.context.syncObjects === undefined) return;

    let animationName;

    this.timeOffset += 1000 * dt;
    // below formula comes from : ((1 + 3 / 60 / dt) / 4) * 0.15 * this.timeOffset
    const timeFactor = (0.0375 + 0.001875 / dt) * this.timeOffset;
    let alpha = clamp(dt * (5 + timeFactor), 0, 1);

    Object.values(this.context.syncObjects).forEach(object => {
      if (object.stateBuffer.length > 2) {
        object.stateBuffer.splice(0, object.stateBuffer.length - 2);
      }
      if (object.stateBuffer.length >= 2) {
        const posAlpha = object.stateBuffer[1].ignorePositionLerping ? 1 : alpha;
        const rotAlpha = object.stateBuffer[1].ignoreRotationLerping ? 1 : alpha;

        if (object.userData.type !== "avatar") {
          this._vector1.copy(object.stateBuffer[1].pos);
          object.position.lerp(this._vector1, posAlpha);

          this._quaternion1.copy(object.stateBuffer[1].quat);
          object.quaternion.slerp(this._quaternion1, rotAlpha);

          if (object.body) object.body.needUpdate = true;
        } else {
          object.manager.render(dt);

          object.body.transform();

          this._vector1.copy(object.body.position);
          this._vector2.copy(object.stateBuffer[1].pos);
          this._vector1.lerp(this._vector2, posAlpha);

          this._quaternion1.copy(object.quaternion);
          this._quaternion2.copy(object.stateBuffer[1].quat);
          this._quaternion1.slerp(this._quaternion2, rotAlpha);
          this._euler.setFromQuaternion(this._quaternion1);

          object.body.setPosition(this._vector1.x, this._vector1.y, this._vector1.z);
          object.body.setRotation(this._euler.x, this._euler.y, this._euler.z);

          object.body.refresh();

          if (object.manager.customAnimation) {
            animationName = object.manager.customAnimation;
            object.manager.isJumpAnimationPlaying = false;
          } else if (object.manager.isJumpAnimationPlaying === false || object.manager.isJumpAnimationPlaying === undefined) {
            if (
              object.stateBuffer[1].pos.x === object.stateBuffer[0].pos.x &&
              object.stateBuffer[1].pos.y === object.stateBuffer[0].pos.y &&
              object.stateBuffer[1].pos.z === object.stateBuffer[0].pos.z
            ) {
              animationName = AVATAR_ANIMATION.IDLE;
              const emote = object.manager.emoteAnimation;
              if (emote) {
                animationName = emote;
              }
            } else {
              if (object.manager.speedFactor > 1) animationName = AVATAR_ANIMATION.BOOST;
              else animationName = AVATAR_ANIMATION.RUN;
            }
          }
          animationName && object.manager.playAnimation(animationName);
          object.manager.animationMixer.update(dt);
        }
      }
    });
  }
}

export default GameServerV1;
